import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetCoachingCenterDetailsQuery,
  useUpdateCoachingCenterMutation,
} from "../../slices/coachingCenterApiSlice";

const CoachingCenterEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("basic");
  const [subscriptionExpiry, setSubscriptionExpiry] = useState("");
  const [isActive, setIsActive] = useState(true);

  const {
    data: coachingCenter,
    isLoading,
    error,
  } = useGetCoachingCenterDetailsQuery(id);

  const [updateCoachingCenter, { isLoading: loadingUpdate }] =
    useUpdateCoachingCenterMutation();

  useEffect(() => {
    if (coachingCenter) {
      setName(coachingCenter.name || "");
      setContactPerson(coachingCenter.contactPerson || "");
      setPhone(coachingCenter.phone || "");
      setEmail(coachingCenter.email || "");
      setAddress(coachingCenter.address || "");
      setSubscriptionPlan(coachingCenter.subscriptionPlan || "basic");

      setSubscriptionExpiry(
        coachingCenter.subscriptionExpiry
          ? new Date(coachingCenter.subscriptionExpiry)
              .toISOString()
              .split("T")[0]
          : "",
      );

      setIsActive(coachingCenter.isActive ?? true);
    }
  }, [coachingCenter]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!name || !contactPerson || !phone) {
      toast.error("Name, Contact Person and Phone are required");
      return;
    }

    try {
      await updateCoachingCenter({
        coachingCenterId: id,

        name,
        contactPerson,
        phone,
        email,
        address,
        subscriptionPlan,
        subscriptionExpiry: subscriptionExpiry || null,
        isActive,
      }).unwrap();

      toast.success("Coaching center updated successfully");

      navigate("/admin/coachingcenters");
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Failed to update coaching center",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/admin/coachingcenters"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-white shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Coaching Centers
        </Link>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Edit Coaching Center
            </h1>

            <p className="text-gray-500 mt-2">
              Update coaching center information
            </p>
          </div>

          {loadingUpdate && (
            <div className="mb-6 rounded-xl bg-blue-100 text-blue-700 p-4 text-center">
              Updating coaching center...
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-10 text-gray-600">
              Loading coaching center...
            </div>
          ) : error ? (
            <div className="rounded-xl bg-red-100 text-red-700 p-4 text-center">
              {error?.data?.message || error.error}
            </div>
          ) : (
            <form
              onSubmit={submitHandler}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {/* Name */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Coaching Center Name *
                </label>

                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contact Person */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Contact Person *
                </label>

                <input
                  type="text"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Phone *
                </label>

                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Email
                </label>

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Subscription Plan */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Subscription Plan
                </label>

                <select
                  value={subscriptionPlan}
                  onChange={(e) => setSubscriptionPlan(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Subscription Expiry */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Subscription Expiry
                </label>

                <input
                  type="date"
                  value={subscriptionExpiry}
                  onChange={(e) => setSubscriptionExpiry(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Status
                </label>

                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Address
                </label>

                <textarea
                  rows={4}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loadingUpdate}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 transition"
                >
                  {loadingUpdate
                    ? "Updating Coaching Center..."
                    : "Update Coaching Center"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachingCenterEditScreen;
